import type { ReactNode } from "react";
import { isValidElement, createElement } from "react";

import type { Components } from "react-markdown";

import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters";

import { getBaseMarkdownComponents } from "../../../utils/markdownComponents";
import type { EnrichedContentJsonObject } from "../types/content";
import type { MdComponentProps } from "../types/mdComponentProps";

import type { IComponentFactoryContext } from "./componentFactories";
import { getComponentForJsonObject } from "./jsonObjectParser";

const BASE_MARKDOWN_COMPONENTS = getBaseMarkdownComponents({});

/**
 * Creates a stateful JSON index tracker that's fresh for each component tree
 * This avoids closure issues by creating a new instance per useMemo recalculation
 * And by resetting per each component tree pass
 */
const createJsonIndexTracker = () => {
    let currentIndex = 0;

    return {
        getNextIndex: () => currentIndex++,
        reset: () => {
            currentIndex = 0;
        },
    };
};

/**
 * Process markdown components with pre-processed JSON data
 */
export const mapComponentsWithPreprocessedData = (
    componentProps: MdComponentProps<LeadsSearchAllFiltersTypes>,
    preprocessedJsonObjects: readonly EnrichedContentJsonObject[],
    messageId: string,
    loader?: ReactNode,
): Components => {
    const indexTracker = createJsonIndexTracker();

    const factoryContext: IComponentFactoryContext = {
        messageId,
        componentProps,
        loader,
    };

    const components: Components = {
        ...BASE_MARKDOWN_COMPONENTS,

        pre: ({ children, className }) => {
            if (isValidElement(children)) {
                const childProps = children.props as { className?: string };

                if (childProps.className?.includes("language-json")) {
                    const currentIndex = indexTracker.getNextIndex();

                    if (currentIndex >= preprocessedJsonObjects.length) {
                        return null;
                    }

                    const jsonObj = preprocessedJsonObjects[currentIndex];

                    const result = getComponentForJsonObject(jsonObj, currentIndex, factoryContext);

                    if (currentIndex === preprocessedJsonObjects.length - 1) {
                        indexTracker.reset();
                    }

                    return result;
                }
            }

            const componentOrElement = BASE_MARKDOWN_COMPONENTS.pre ?? "pre";
            return createElement(componentOrElement, { children, className });
        },
    };

    return components;
};
